import Compressor from "compressorjs";
import moment from "moment";
import "moment/dist/locale/es";
import { filter, includes, isEmpty, join, map, replace, split, startsWith, toLower, trim, get } from "lodash";

moment.locale("es");
export const compressImage = (image) => {
	return new Promise((resolve, reject) => {
		new Compressor(image, {
			quality: 0.6,
			maxHeight: 1024,
			maxWidth: 1024,
			success(result) {
				resolve(result);
			},
			error(err) {
				reject(err);
			},
		});
	});
};

export const getFileExt = (name = "") => {
	const parts = split(name, ".");
	return toLower(parts.length > 1 ? parts.pop() : "");
};

export const getFileTypeFromDataUrl = (dataUrl = "") => {
	const match = String(dataUrl).match(/^data:([^;]+);base64,/i);
	const mime = match ? toLower(match[1]) : "";
	if (startsWith(mime, "image/")) return "image";
	if (startsWith(mime, "audio/")) return "audio";
	if (startsWith(mime, "video/")) return "video";
	if (mime === "application/pdf" || includes(mime, "pdf")) return "pdf";
	if (includes(mime, "word") || includes(mime, "officedocument.wordprocessingml")) return "doc";
	if (includes(mime, "excel") || includes(mime, "spreadsheetml")) return "xls";
	if (includes(mime, "powerpoint") || includes(mime, "presentationml")) return "ppt";
	if (includes(mime, "zip") || includes(mime, "x-7z-compressed") || includes(mime, "x-rar-compressed")) return "zip";
	if (includes(mime, "text/")) return "txt";
	return "file";
};

const buildLookupString = (entries = []) => {
	return ` ${join(entries, " ")} `;
};

const hasAnyExtension = (lookup, ext) => {
	const normalized = ` ${trim(toLower(ext))} `;
	return includes(lookup, normalized);
};

const docLookup = buildLookupString(["doc", "docx"]);
const sheetLookup = buildLookupString(["xls", "xlsx", "csv"]);
const deckLookup = buildLookupString(["ppt", "pptx"]);
const archiveLookup = buildLookupString(["zip", "rar", "7z"]);
const textLookup = buildLookupString(["txt", "md", "rtf"]);
const imageLookup = buildLookupString(["jpg", "jpeg", "png", "webp", "apng", "avif", "gif", "svg"]);

export const getFileTypeByName = (name = "", mime = "") => {
	const mimeLower = toLower(mime);
	if (mimeLower && startsWith(mimeLower, "image/")) return "image";
	if (mimeLower && startsWith(mimeLower, "audio/")) return "audio";
	if (mimeLower && startsWith(mimeLower, "video/")) return "video";
	const ext = getFileExt(name);
	if (ext === "pdf") return "pdf";
	if (hasAnyExtension(docLookup, ext)) return "doc";
	if (hasAnyExtension(sheetLookup, ext)) return "xls";
	if (hasAnyExtension(deckLookup, ext)) return "ppt";
	if (hasAnyExtension(archiveLookup, ext)) return "zip";
	if (hasAnyExtension(textLookup, ext)) return "txt";
	if (hasAnyExtension(imageLookup, ext)) return "image";
	return "file";
};

export const guidGenerator = () => {
	const S4 = () => {
		// eslint-disable-next-line no-bitwise
		return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
	};
	return `${S4()}${S4()}-${S4()}-${S4()}-${S4()}-${S4()}${S4()}${S4()}`;
};

export const getImageSrcFromBase64 = (base64String, mimeType = "image/png") => {
	if (!base64String) return "";
	return `data:${mimeType};base64,${base64String}`;
};

export const buildURLQuery = (obj) => {
	Object.entries(obj)
		.map((pair) => {
			return pair.map(encodeURIComponent).join("=");
		})
		.join("&");
};

export const getTimeStamp = () => {
	return moment().valueOf();
};

export const autoCapitalize = (text) => {
	if (!text) return text;
	return String(text).replace(/(^\s*[¿¡]?\s*|[.!?]+\s+[¿¡]?\s*)([\p{Ll}])(?=[\s\S])/gu, (match, prefix, letter) => {
		return prefix + letter.toUpperCase();
	});
};

export const getTimeLabel = (time, format) => {
	if (time) {
		const momentInstance = moment(time);
		momentInstance.locale("es");
		const formattedDate = momentInstance.format(format);
		return replace(formattedDate, /^\w/, (c) => {
			return c.toUpperCase();
		});
	}
	return "";
};

export const messageBuilder = ({ chat = {}, type = "Text", reply = {}, userName = "", text = "", uri = null, messageId = null }) => {
	const newMessage = {
		id: messageId || guidGenerator(),
		chatId: chat?.id,
		channel: chat?.channel,
		channelCid: chat?.channelCid,
		timestamp: moment().valueOf(),
		createdDate: moment().format("YYYY-MM-DDTHH:mm:ss"),
		type,
		text,
		uri,
		reply,
		sentByAgent: true,
		userName,
	};
	return newMessage;
};

export function getTimeLabelTodayComplete(time, dateOnly = false, hourOnly = false, both = false) {
	const propTime = new Date(time);
	if (hourOnly)
		return propTime.toLocaleString("en-GB", {
			hour12: true,
			hour: "2-digit",
			minute: "2-digit",
		});
	if (dateOnly) {
		return propTime.toLocaleDateString("en-GB", { date: "short" });
	}
	if (both)
		return propTime.toLocaleString("en-GB", {
			hour12: true,
			day: "2-digit",
			month: "2-digit",
			year: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
		});
	return propTime.toLocaleString("en-GB", {
		day: "2-digit",
		month: "2-digit",
		year: "2-digit",
	});
}

export function onTableSearch(data = null, searchValue = null, hotKey = null) {
	if (!data) return [];

	try {
		let hotKeyFound = false;

		const filtered = filter(data, (item) => {
			const obj = {};

			Object.keys(item).forEach((key) => {
				obj[key] = item[key];
			});

			if (hotKey) if (obj[hotKey] === searchValue) hotKeyFound = true;

			Object.keys(obj).forEach((key) => {
				const value = obj[key];
				const re = new RegExp(`W*(${searchValue})W*`);

				if (value === searchValue) {
					return true;
				}
				if (re.test(value ? value.toString().toLowerCase() : value)) {
					return true;
				}
				if (re.test(value)) {
					return true;
				}
				if (searchValue.toString().trim().split(" ").length > 0) {
					const wordsKeyboard = searchValue.toString().trim().split(" ");
					const valueWords = value ? value.toString().trim().split(" ") : "";
					let matchedWoords = 0;
					wordsKeyboard.forEach((_item, index) => {
						const reqq = new RegExp(`W*(${searchValue})W*`);
						if (reqq.test(valueWords[index] ? valueWords[index].toString().toLowerCase() : valueWords[index])) matchedWoords += 1;
						else if (reqq.test(valueWords[index] ? valueWords[index] : valueWords[index])) matchedWoords += 1;
					});
					if (matchedWoords === wordsKeyboard.length) return true;
				}
			});
		});
		if (hotKeyFound)
			return filtered.filter((item) => {
				return item[hotKey] === searchValue;
			});
		return filtered;
	} catch (e) {
		return data;
	}
}

export const getValidationErrors = (validation, defaultErrors) => {
	const { details } = validation.error;
	let errors = defaultErrors;
	map(details, (item) => {
		errors = { ...errors, [item.context.key]: item.message };
	});
	return errors;
};

export function timeBetweenDatesCurrent(time, timeEnd) {
	const today = new Date(time);
	let endDate = new Date();
	if (!isEmpty(time)) {
		if (!isEmpty(timeEnd)) {
			endDate = new Date(timeEnd);
		}

		const days = parseInt((endDate - today) / (1000 * 60 * 60 * 24), 10);
		const hours = parseInt((Math.abs(today - endDate) / (1000 * 60 * 60)) % 24, 10);
		const minutes = parseInt((Math.abs(endDate.getTime() - today.getTime()) / (1000 * 60)) % 60, 10);
		const seconds = parseInt((Math.abs(endDate.getTime() - today.getTime()) / 1000) % 60, 10);

		let timeString = "";
		if (days === 0) {
			if (hours === 0) {
				if (minutes === 0) {
					timeString = `${String(seconds)}s`;
				} else {
					timeString = `${String(minutes)}m  ${String(seconds)}s`;
				}
			} else {
				timeString = `${String(hours)}h  ${String(minutes)}m  ${String(seconds)}s`;
			}
		} else {
			timeString = `${String(days)}d  ${String(hours)}h  ${String(minutes)}m  ${String(seconds)}s`;
		}
		return timeString;
	}
	return "";
}

export const getAgentTimestamp = (messages) => {
	if (!Array.isArray(messages) || messages.length === 0) return null;

	let firstClientMsg = null;

	// eslint-disable-next-line no-plusplus
	for (let i = 0; i < messages.length; i++) {
		const msg = messages[i];

		if (msg.sentByAgent === false) {
			firstClientMsg = msg;
		} else if (msg.sentByAgent === true && firstClientMsg) {
			break;
		}
	}

	return firstClientMsg?.timestamp || null;
};

export const getChatAlertName = (chat, defaultAlerts, isHistoric = false) => {
	if (isEmpty(chat) || isHistoric || get(chat, "message.sentByAgent")) {
		return get(chat, "closeState", "A Tiempo");
	}

	const lastTime = getAgentTimestamp(chat.messages);
	const alerts = get(chat, "group.settings.alerts", defaultAlerts);
	if (!lastTime || alerts?.length < 3) return "A Tiempo";

	const diff = (Date.now() - lastTime) / 60000;
	return (
		[alerts[2], alerts[1]].find((a) => {
			return diff >= a.time;
		})?.name || alerts[0].name
	);
};
